import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Check, Users, Info } from 'lucide-react';
import * as skillsService from '../../../../utils/onboardingV2/skillsService.ts';
import styles from './ProfileConnectSelector.module.scss';

interface SkillCategory {
  id: number;
  name: string;
}

interface ProfileConnectSelectorProps {
  selectedCategories: number[];
  onChange: (categories: number[]) => void;
}

const ProfileConnectSelector: React.FC<ProfileConnectSelectorProps> = ({
  selectedCategories,
  onChange,
}) => {
  const [categories, setCategories] = useState<SkillCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const data = await skillsService.fetchCategories();
        setCategories(data);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  const handleCategoryClick = (categoryId: number) => {
    const isSelected = selectedCategories.includes(categoryId);
    const updated = isSelected
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    onChange(updated);
  };

  const selectedCategoriesNames = useMemo(() => {
    return selectedCategories
      .map((id) => categories.find((cat) => cat.id === id)?.name)
      .filter(Boolean);
  }, [selectedCategories, categories]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Cargando categorías...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Users size={16} />
        <span>¿Con qué perfiles querés conectar?</span>
      </div>

      <div className={styles.infoMessage}>
        <Info size={16} />
        <span>Seleccioná las categorías de profesionales o empresas con las que te gustaría conectar en Edify.</span>
      </div>

      {selectedCategories.length > 0 && (
        <div className={styles.selectedSummary}>
          <span className={styles.selectedSummary_label}>Seleccionados:</span>
          <span className={styles.selectedSummary_count}>{selectedCategories.length} categoría{selectedCategories.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      <div className={styles.categoriesGrid}>
        {categories.map((category) => {
          const isSelected = selectedCategories.includes(category.id);
          return (
            <motion.button
              key={category.id}
              type="button"
              className={`${styles.categoryPill} ${isSelected ? styles.active : ''}`}
              onClick={() => handleCategoryClick(category.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              {isSelected && <Check size={16} className={styles.checkIcon} />}
              {category.name}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

export default ProfileConnectSelector;
